import React, { useEffect, useState, useRef } from 'react';
import { youtubeNotificationService, YouTubeNotification } from '../../services/youtubeNotificationService';
import { useToast } from '../../components/Toast';
import './AdminYouTubeNotificationsPage.css';

const AdminYouTubeNotificationsPage: React.FC = () => {
  const { showToast } = useToast();
  const [notifications, setNotifications] = useState<YouTubeNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);

  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<YouTubeNotification>>({
    stt: 0,
    quan_doan: '',
    mon_phai: '',
    viral_key: '',
    rpm: '',
    title: '',
    views: 0,
    ngay_dang: '',
    youtube_link: '',
    is_active: true,
    display_order: 0,
  });
  
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchNotifications();
  }, [page, search, isActiveFilter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await youtubeNotificationService.list({
        page,
        per_page: perPage,
        search: search || undefined,
        is_active: isActiveFilter || undefined,
      });
      setNotifications(data.notifications);
      setTotal(data.total);
    } catch (error: any) {
      showToast('Lỗi khi tải danh sách thông báo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await youtubeNotificationService.create(formData);
      showToast('Tạo thông báo thành công', 'success');
      setShowCreateForm(false);
      resetForm();
      fetchNotifications();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi tạo thông báo', 'error');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    try {
      await youtubeNotificationService.update(editingId, formData);
      showToast('Cập nhật thành công', 'success');
      setEditingId(null);
      resetForm();
      fetchNotifications();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi cập nhật', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc muốn xóa thông báo này?')) return;
    try {
      await youtubeNotificationService.delete(id);
      showToast('Xóa thành công', 'success');
      setSelectedNotifications(selectedNotifications.filter(nId => nId !== id));
      fetchNotifications();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.length === 0) {
      showToast('Vui lòng chọn ít nhất một thông báo để xóa', 'warning');
      return;
    }

    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedNotifications.length} thông báo đã chọn?`)) return;

    try {
      const result = await youtubeNotificationService.bulkDelete(selectedNotifications);
      showToast(result.message, 'success');
      setSelectedNotifications([]);
      setSelectAll(false);
      fetchNotifications();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa hàng loạt', 'error');
    }
  };

  const handleSelectNotification = (id: number) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(selectedNotifications.filter(nId => nId !== id));
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
      setSelectAll(false);
    } else {
      const allIds = notifications.map(n => n.id!).filter(id => id !== undefined);
      setSelectedNotifications(allIds);
      setSelectAll(true);
    }
  };

  // Update selectAll state when selectedNotifications changes
  useEffect(() => {
    if (notifications.length > 0) {
      const allSelected = notifications.every(n => n.id && selectedNotifications.includes(n.id));
      setSelectAll(allSelected && selectedNotifications.length === notifications.length);
    }
  }, [selectedNotifications, notifications]);

  const handleEdit = async (id: number) => {
    try {
      const notification = await youtubeNotificationService.get(id);
      setFormData({
        stt: notification.stt,
        quan_doan: notification.quan_doan || '',
        mon_phai: notification.mon_phai || '',
        viral_key: notification.viral_key || '',
        rpm: notification.rpm || '',
        title: notification.title,
        views: notification.views,
        ngay_dang: notification.ngay_dang || '',
        youtube_link: notification.youtube_link,
        is_active: notification.is_active,
        display_order: notification.display_order,
      });
      setEditingId(id);
      setShowCreateForm(true);
    } catch (error: any) {
      showToast('Lỗi khi tải thông tin', 'error');
    }
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportProgress(0);
    try {
      const result = await youtubeNotificationService.importExcel(file, (progress) => {
        setImportProgress(progress);
      });
      showToast(
        `Import thành công ${result.created} thông báo${result.errors?.length ? `. ${result.errors.length} lỗi` : ''}`,
        result.errors?.length ? 'warning' : 'success'
      );
      if (result.errors && result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }
      fetchNotifications();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi import', 'error');
    } finally {
      setImporting(false);
      setImportProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await youtubeNotificationService.exportExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'youtube_notifications.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('Export thành công', 'success');
    } catch (error: any) {
      showToast('Lỗi khi export', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      stt: 0,
      quan_doan: '',
      mon_phai: '',
      viral_key: '',
      rpm: '',
      title: '',
      views: 0,
      ngay_dang: '',
      youtube_link: '',
      is_active: true,
      display_order: 0,
    });
    setEditingId(null);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="admin-youtube-notifications-page">
      <div className="page-header">
        <h1>Quản lý Thông báo YouTube</h1>
        <div className="header-actions">
          {selectedNotifications.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="btn btn-danger"
            >
              Xóa đã chọn ({selectedNotifications.length})
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="btn btn-primary"
          >
            + Thêm mới
          </button>
          <label className="btn btn-secondary">
            {importing ? `Đang import... ${importProgress}%` : 'Import Excel'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              disabled={importing}
              style={{ display: 'none' }}
            />
          </label>
          <button onClick={handleExportExcel} className="btn btn-secondary">
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Tìm kiếm..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="search-input"
        />
        <select
          value={isActiveFilter}
          onChange={(e) => {
            setIsActiveFilter(e.target.value);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="">Tất cả</option>
          <option value="true">Đang hiển thị</option>
          <option value="false">Đã ẩn</option>
        </select>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateForm(false);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? 'Chỉnh sửa' : 'Thêm mới'} Thông báo</h2>
            <form onSubmit={editingId ? handleUpdate : handleCreate}>
              <div className="form-grid">
                <div className="form-group">
                  <label>STT *</label>
                  <input
                    type="number"
                    value={formData.stt || 0}
                    onChange={(e) => setFormData({ ...formData, stt: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Quân đoàn</label>
                  <input
                    type="text"
                    value={formData.quan_doan || ''}
                    onChange={(e) => setFormData({ ...formData, quan_doan: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Môn Phái (Ngách)</label>
                  <input
                    type="text"
                    value={formData.mon_phai || ''}
                    onChange={(e) => setFormData({ ...formData, mon_phai: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Từ khóa long mạch (Viral key)</label>
                  <input
                    type="text"
                    value={formData.viral_key || ''}
                    onChange={(e) => setFormData({ ...formData, viral_key: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>RPM</label>
                  <input
                    type="text"
                    value={formData.rpm || ''}
                    onChange={(e) => setFormData({ ...formData, rpm: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Tiêu đề *</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Views</label>
                  <input
                    type="number"
                    value={formData.views || 0}
                    onChange={(e) => setFormData({ ...formData, views: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Ngày đăng</label>
                  <input
                    type="date"
                    value={formData.ngay_dang || ''}
                    onChange={(e) => setFormData({ ...formData, ngay_dang: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Link YouTube *</label>
                  <input
                    type="url"
                    value={formData.youtube_link || ''}
                    onChange={(e) => setFormData({ ...formData, youtube_link: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Thứ tự hiển thị</label>
                  <input
                    type="number"
                    value={formData.display_order || 0}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Đang hiển thị
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Cập nhật' : 'Tạo mới'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Đang tải...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    title="Chọn tất cả"
                  />
                </th>
                <th>STT</th>
                <th>Quân đoàn</th>
                <th>Môn Phái</th>
                <th>Viral key</th>
                <th>RPM</th>
                <th>Tiêu đề</th>
                <th>Views</th>
                <th>Ngày đăng</th>
                <th>Link YouTube</th>
                <th>Trạng thái</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification) => (
                <tr key={notification.id} className={selectedNotifications.includes(notification.id!) ? 'selected-row' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.id!)}
                      onChange={() => handleSelectNotification(notification.id!)}
                    />
                  </td>
                  <td>{notification.stt}</td>
                  <td>{notification.quan_doan || '-'}</td>
                  <td>{notification.mon_phai || '-'}</td>
                  <td>{notification.viral_key || '-'}</td>
                  <td>{notification.rpm || '-'}</td>
                  <td className="title-cell">{notification.title}</td>
                  <td>{notification.views.toLocaleString()}</td>
                  <td>{notification.ngay_dang ? new Date(notification.ngay_dang).toLocaleDateString('vi-VN') : '-'}</td>
                  <td>
                    <a href={notification.youtube_link} target="_blank" rel="noopener noreferrer" className="link">
                      Xem
                    </a>
                  </td>
                  <td>
                    <span className={`badge ${notification.is_active ? 'active' : 'inactive'}`}>
                      {notification.is_active ? 'Hiển thị' : 'Ẩn'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(notification.id!)} className="btn-icon" title="Sửa">
                        ✏️
                      </button>
                      <button onClick={() => handleDelete(notification.id!)} className="btn-icon" title="Xóa">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Trước
          </button>
          <span>
            Trang {page} / {totalPages} (Tổng: {total})
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Sau
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminYouTubeNotificationsPage;

